import type { LedgerEntry, PeriodId } from '@/domain'

import type { Database } from './database.types'
import type { CentimeClient } from './supabase'

type EntryRow = Database['public']['Tables']['ledger_entries']['Row']

/** Convertit une ligne du journal en écriture du domaine. La base garantit la forme (CHECK). */
export function entryFromRow(row: EntryRow): LedgerEntry {
  const base = {
    operationId: row.operation_id,
    line: row.line,
    periodId: row.period_id,
    occurredAt: row.occurred_at,
  }
  const amount = row.amount ?? 0
  switch (row.kind) {
    case 'income':
      return {
        ...base,
        kind: 'income',
        amount,
        title: row.title ?? '',
        occurrenceId: row.occurrence_id,
      }
    case 'allocation':
      return { ...base, kind: 'allocation', envelopeId: row.envelope_id ?? '', amount }
    case 'expense':
      return {
        ...base,
        kind: 'expense',
        envelopeId: row.envelope_id ?? '',
        amount,
        title: row.title ?? '',
        occurrenceId: row.occurrence_id,
      }
    case 'transfer':
      return {
        ...base,
        kind: 'transfer',
        sourceEnvelopeId: row.envelope_id ?? '',
        destinationEnvelopeId: row.counter_envelope_id ?? '',
        amount,
        origin: row.transfer_origin === 'shortfall' ? 'shortfall' : 'manual',
        note: row.note,
      }
    case 'reservation':
    case 'release':
      return {
        ...base,
        kind: row.kind,
        envelopeId: row.envelope_id ?? '',
        amount,
        occurrenceId: row.occurrence_id ?? '',
      }
    case 'adjustment':
      return {
        ...base,
        kind: 'adjustment',
        envelopeId: row.envelope_id,
        direction: row.adjustment_direction === 'debit' ? 'debit' : 'credit',
        amount,
        reason: row.reason ?? '',
      }
    case 'reversal':
      return {
        ...base,
        kind: 'reversal',
        reversedOperationId: row.reversed_operation_id ?? '',
        reason: row.reason ?? '',
      }
  }
}

/** Écritures d'une période, dans l'ordre chronologique. */
export async function fetchPeriodEntries(
  client: CentimeClient,
  periodId: PeriodId,
): Promise<LedgerEntry[]> {
  const { data, error } = await client
    .from('ledger_entries')
    .select('*')
    .eq('period_id', periodId)
    .order('occurred_at')
    .order('operation_id')
    .order('line')
  if (error) throw error
  return data.map(entryFromRow)
}
